import Export from "@/components/Export";
import SearchBox from "@/components/SearchBox";
import {
  useAllLookups,
  useCreateLookup,
  useLookups,
  useUpdateLookup,
} from "@/hooks/useLookups";
import { ILookup } from "@/interfaces/lookups.interface";
import { RootState } from "@/store/store";
import { buildFilters, getPaginationData } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { Button } from "@progress/kendo-react-buttons";
import { columnProps } from "@/components/ColumnMenu";
import { useSearchFilter } from "@/hooks/useSearchFilters";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Switch, TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Plus } from "lucide-react";
import { createRef, useEffect, useMemo, useState } from "react";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { useAppSelector } from "../../store/hooks/hooks";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";

let mappingChanged = false;

export const Listing = ({ lookupType }: { lookupType: string }) => {
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();

  const createLookupMutation = useCreateLookup();
  const updateLookupMutation = useUpdateLookup();

  const [addNewLookup, setAddNewLookup] = useState<
    (ILookup & { id?: string }) | null
  >(null);
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showDrawer, setShowDrawer] = useState(false);
  const [lookups, setLookups] = useState<ILookup[]>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(handleEditClick, onColumnsSubmit, null, enableDisableLookup)
  );
  const [showArchived, setShowArchived] = useState(false);

  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["value"],
    });

  const filterParams = useMemo(
    () => ({
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort,
      filter: buildFilters(
        selectedProject?.id,
        dataState.filter,
        showArchived,
        [{ field: "type", operator: "eq", value: lookupType }]
      ),
    }),
    [
      page.skip,
      page.take,
      dataState.sort,
      dataState.filter,
      showArchived,
      lookupType,
      selectedProject,
    ]
  );

  const { data: lookupsData, refetch } = useLookups(filterParams);

  const { data: allLookupsData, refetch: refetchAllLookups } = useAllLookups(
    { filter: buildFilters(selectedProject?.id) },
    { enabled: false }
  );

  const excelExport = () => {
    if (_exporter.current) {
      if (lookupsData?.data && lookupsData.data.length > 0) {
        // Get the formatted data

        // Save with the formatted data
        _exporter.current.save(lookupsData?.data);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            _exporter.current?.save(lookupsData?.data || []);
          }, 300);
        });
      }
    }
  };
  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      enableDisableLookup
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? lookups.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleChange = (e: TextBoxChangeEvent | ComboBoxChangeEvent) => {
    let val = e.target.value;
    if (e.target.name === "projectId") {
      val = e.value.id;
    }

    setAddNewLookup((prev: any) => {
      return { ...prev, [e.target.name as any]: val };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const createLookup = () => {
    if (!addNewLookup) return;

    if (!selectedProject || !selectedProject.id) {
      ToastService.error("Please select a project first");
      return;
    }

    createLookupMutation.mutate(
      {
        ...addNewLookup,
        type: lookupType,
        isActive: true,
        projectId: selectedProject.id,
      },
      {
        onSuccess: (data) => {
          ToastService.success("Lookup created successfully");
          setShowDrawer(false);
        },
        onError: (data) => {
          // ToastService.error(data?.data?.message);
        },
      }
    );
  };

  const editLookup = async () => {
    if (!addNewLookup?.id) return;

    updateLookupMutation.mutate(
      { id: addNewLookup.id, data: addNewLookup },
      {
        onSuccess: () => {
          ToastService.success("Lookup updated successfully");
          setShowDrawer(false);
        },
        onError: (data) => {
          // ToastService.error(data?.data?.message);
        },
      }
    );
  };

  function enableDisableLookup(dataItem: ILookup) {
    if (!dataItem?.id) return;
    updateLookupMutation.mutate(
      { id: dataItem.id, data: { ...dataItem, isActive: !dataItem.isActive } },
      {
        onSuccess: () => {
          ToastService.success("Lookup updated successfully");
        },
        onError: (data) => {
          // ToastService.error(data?.data?.message);
        },
      }
    );
  }

  function handleEditClick(data: ILookup) {
    setAddNewLookup(data);
    setShowDrawer(true);
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewLookup(null);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (lookupsData?.data) {
      setLookups(lookupsData.data);
    }
  }, [lookupsData]);

  useEffect(() => {
    if (!lookups?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableLookup
    );
    setStateColumns(cols);
  }, [lookups, columnsMapping]);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={lookups}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={lookupsData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => setShowDrawer(true)}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>
              <div className="flex items-center gap-2 md:order-3 sm:order-2 order-4 sm:mt-0 mt-2 sm:ml-0 ml-auto">
                <Label className="font-medium text-sm font-cabin">
                  Show Inactive
                </Label>
                <Switch
                  defaultChecked={false}
                  onChange={(e) => {
                    setShowArchived(e.value);
                  }}
                />
              </div>
              <div
                className="cursor-pointer sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allLookupsData?.data || []}
                  collapsible={true}
                  fileName={`${lookupType}.xlsx`}
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      {...columnProps(column.field, dataState)}
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    {...columnProps(column.field, dataState)}
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      <WriteDrawer
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        addNewLookup={addNewLookup}
        handleChange={handleChange}
        onSave={addNewLookup?.id ? editLookup : createLookup}
      />
    </>
  );
};
